import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiImage, FiTrash2, FiUpload } from 'react-icons/fi';
import { groupMeta } from './constants';

// APP_LOGO is a setting like any other, but its value is a file — so it gets a
// row of the same shape as the rest instead of a special panel somewhere else.
const LogoRow = ({ logoSetting, onUpload, onDelete }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const logoUrl = logoSetting?.logo_url;
  // The URL is stable across replacements, so without a cache-buster the
  // browser keeps showing the logo that was just replaced.
  const cacheKey = logoSetting?.updated_at || '';
  const meta = groupMeta('ui');

  return (
    <div className="tw-flex tw-items-stretch tw-border-b tw-border-slate-100 hover:tw-bg-slate-50/70 tw-transition-colors">
      <div className="tw-w-0.5 tw-shrink-0" />
      <div className="tw-flex-1 tw-min-w-0 tw-grid tw-gap-x-6 tw-gap-y-3 tw-px-5 tw-py-4 tw-grid-cols-1 lg:tw-grid-cols-[minmax(0,34%)_minmax(0,1fr)]">

        <div className="tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
            <FiImage size={11} className="tw-text-slate-400 tw-shrink-0" />
            <span className="tw-font-mono tw-text-[12.5px] tw-font-semibold tw-text-slate-800">APP_LOGO</span>
          </div>
          <p className="tw-text-[12px] tw-text-slate-500 tw-leading-snug tw-mb-1.5">
            Application logo shown in the portal header
          </p>
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-border-slate-200 tw-bg-white tw-text-[10px] tw-font-semibold tw-text-slate-600">
            <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full" style={{ background: meta.dot }} />
            ui
          </span>
        </div>

        <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap tw-min-w-0">
          <div className="tw-h-11 tw-w-28 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-shrink-0">
            {logoUrl
              ? <img src={`${logoUrl}${logoUrl.includes('?') ? '&' : '?'}_t=${cacheKey}`} alt="Application logo" className="tw-max-h-full tw-max-w-full tw-object-contain tw-p-1.5" />
              : <FiImage className="tw-text-slate-300" size={18} />}
          </div>

          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <label
              className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 tw-cursor-pointer tw-transition-colors tw-mb-0 ${
                uploading ? 'tw-opacity-50 tw-pointer-events-none' : ''
              }`}
            >
              {uploading
                ? <span className="tw-w-3 tw-h-3 tw-border tw-border-slate-400 tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                : <FiUpload size={11} />}
              {uploading ? 'Uploading…' : 'Upload image'}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/svg+xml,image/jpeg,image/webp"
                onChange={handleFile}
                disabled={uploading}
                className="tw-hidden"
              />
            </label>

            {logoUrl && !uploading && (
              <button
                type="button"
                onClick={onDelete}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-red-600 tw-border tw-border-red-200 tw-rounded-lg hover:tw-bg-red-50 tw-bg-white tw-cursor-pointer tw-transition-colors"
              >
                <FiTrash2 size={11} /> Remove
              </button>
            )}
            <span className="tw-text-[11px] tw-text-slate-400">PNG, SVG, JPG or WEBP</span>
          </div>
        </div>
      </div>
    </div>
  );
};

LogoRow.propTypes = {
  logoSetting: PropTypes.shape({
    logo_url: PropTypes.string,
    updated_at: PropTypes.string,
  }),
  onUpload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default LogoRow;
